<template>
  <section
    v-if="visibleAnnouncements.length"
    class="announcement-banner"
    :aria-label="$t('announcements.bannerLabel')">
    <b-alert
      v-for="announcement in visibleAnnouncements"
      :key="announcement.id"
      :variant="announcement.type || 'info'"
      :dismissible="isDismissible(announcement)"
      :dismiss-label="$t('announcements.dismiss')"
      show
      class="announcement-banner__item"
      @dismissed="dismiss(announcement)">
      <v-icon
        :name="iconFor(announcement.type)"
        class="announcement-banner__icon" />
      <span>
        {{ announcement.message }}
        <a
          v-if="safeLinkUrl(announcement)"
          :href="safeLinkUrl(announcement)"
          class="alert-link announcement-banner__link"
          target="_blank"
          rel="noopener noreferrer">
          {{ announcement.linkLabel }}
          <span aria-hidden="true">↗</span>
        </a>
      </span>
    </b-alert>
  </section>
</template>

<script>
import { findActiveAnnouncements } from '../services/announcementService';

const REFRESH_INTERVAL = 5 * 60 * 1000;
const DISMISSED_STORAGE_KEY = 'revatech-dismissed-announcements';
const DISMISSIBLE_TYPES = [ 'info', 'success', 'warning' ];

export default {
  data() {
    return {
      announcements: [],
      dismissedAnnouncements: {},
      refreshTimer: null
    };
  },
  computed: {
    visibleAnnouncements() {
      return this.announcements.filter(announcement => !this.isDismissed(announcement));
    }
  },
  mounted() {
    this.loadDismissedAnnouncements();
    this.loadAnnouncements();
    this.refreshTimer = window.setInterval(this.loadAnnouncements, REFRESH_INTERVAL);
    window.addEventListener('revatech:announcements-changed', this.loadAnnouncements);
  },
  beforeDestroy() {
    window.clearInterval(this.refreshTimer);
    window.removeEventListener('revatech:announcements-changed', this.loadAnnouncements);
  },
  methods: {
    loadDismissedAnnouncements() {
      const storedDismissals = window.localStorage.getItem(DISMISSED_STORAGE_KEY);
      if (!storedDismissals) return;

      try {
        const parsedDismissals = JSON.parse(storedDismissals);
        this.dismissedAnnouncements = parsedDismissals && typeof parsedDismissals === 'object'
          ? parsedDismissals
          : {};
      } catch (error) {
        this.dismissedAnnouncements = {};
      }
    },
    loadAnnouncements() {
      findActiveAnnouncements()
        .then(announcements => {
          this.announcements = Array.isArray(announcements) ? announcements : [];
        })
        .catch(() => {
          this.announcements = [];
        });
    },
    iconFor(type) {
      if (type === 'danger' || type === 'warning') return 'exclamation-triangle';
      if (type === 'success') return 'check-circle';
      return 'info-circle';
    },
    isDismissible(announcement) {
      return DISMISSIBLE_TYPES.includes(announcement.type || 'info');
    },
    dismissalVersion(announcement) {
      return announcement.updatedAt || announcement.createdAt || announcement.message;
    },
    safeLinkUrl(announcement) {
      if (!announcement.linkUrl || !announcement.linkLabel) return '';

      try {
        const url = new URL(announcement.linkUrl);
        return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : '';
      } catch (error) {
        return '';
      }
    },
    isDismissed(announcement) {
      if (!this.isDismissible(announcement)) return false;
      return this.dismissedAnnouncements[announcement.id] === this.dismissalVersion(announcement);
    },
    dismiss(announcement) {
      if (!this.isDismissible(announcement)) return;

      this.$set(
        this.dismissedAnnouncements,
        announcement.id,
        this.dismissalVersion(announcement)
      );
      window.localStorage.setItem(
        DISMISSED_STORAGE_KEY,
        JSON.stringify(this.dismissedAnnouncements)
      );
    }
  }
};
</script>

<style scoped>
.announcement-banner {
  width: 100%;
}

.announcement-banner__item {
  align-items: center;
  border: 0;
  border-radius: 0;
  display: flex;
  justify-content: center;
  margin: 0;
  min-height: 44px;
  padding: 0.65rem 1rem;
  text-align: center;
}

.announcement-banner__icon {
  flex: 0 0 auto;
  margin-right: 0.65rem;
}

.announcement-banner__link {
  display: inline-block;
  margin-left: 0.5rem;
}
</style>
